// http.js
import axios from 'axios';
import { ElMessage } from 'element-plus';
import { getApiUrl } from '../config.js';
import {initUserActivityTracker, shouldRefreshToken, refreshTokenRequest } from './auth.js';

// 获取API基础URL
const getBaseURL = () => {
  // 检查是否通过ngrok域名访问
  if (typeof window !== 'undefined' && window.location.hostname.includes('ngrok-free.app')) {
    // 通过ngrok访问时使用相对路径，让Vue开发服务器代理
    return '';
  }
  // 本地开发时使用完整URL
  return getApiUrl();
};

// 创建 axios 实例
const instance = axios.create({
  baseURL: getBaseURL(),
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 创建不需要认证的 axios 实例（用于发送验证码等接口）
const noAuthInstance = axios.create({
  baseURL: getBaseURL(),
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
instance.interceptors.request.use(
  config => {
    // 初始化活跃度监听（只需一次）
    initUserActivityTracker();

    // 若用户活跃且 token 即将过期，则尝试在发起业务请求前刷新
    // 为避免循环，此处不对刷新接口本身生效
    try {
      const base = getBaseURL();
      const rawUrl = config && config.url ? String(config.url) : '';
      const url = rawUrl.startsWith(base) ? rawUrl.slice(base.length) : rawUrl;
      const isRefreshApi = /^\s*\/app\/auth\/refresh(\b|\/|\?|#|$)/.test(url);
      if (!isRefreshApi && shouldRefreshToken()) {
        // 通过全局刷新锁进行并发收敛
        pendingRefreshPromise = pendingRefreshPromise || refreshTokenRequest().catch(() => {
          // 失败后清空，便于后续重试；静默处理不打日志
          pendingRefreshPromise = null;
        });
      }
    } catch (_) {
      // ignore
    }
    try {
      // 计算实际相对路径用于白名单判断
      const base = getBaseURL();
      const rawUrl = config && config.url ? String(config.url) : '';
      const url = rawUrl.startsWith(base) ? rawUrl.slice(base.length) : rawUrl;

      // 白名单：登录接口、发送短信接口、微信配置接口、刷新接口、mockLogin（不附带 token）
      const isWhitelisted = (() => {
        // 登录相关：仅限登录动作本身
        if (/^\s*\/app\/auth\/(wechatLogin|bindPhone|mockLogin)(\b|\/|\?|#|$)/.test(url)) return true;
        // 发送短信验证码
        if (/^\s*\/app\/sms\/sendCode(\b|\/|\?|#|$)/.test(url)) return true;
        // 微信JS-SDK配置接口
        if (/^\s*\/app\/wechat\/jsConfig(\b|\/|\?|#|$)/.test(url)) return true;
        // 刷新 token 接口
        if (/^\s*\/app\/auth\/refresh(\b|\/|\?|#|$)/.test(url)) return true;
        return false;
      })();

      // 按需附加本地 token
      if (!isWhitelisted) {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers = config.headers || {};
          // 仅附带 Authorization，避免自定义 token 头导致 CORS 预检失败
          config.headers.Authorization = `Bearer ${token}`;
        }
      } else if (config && config.headers && 'Authorization' in config.headers) {
        // 白名单显式移除 Authorization，防止外部误传
        delete config.headers.Authorization;
      }
    } catch (e) {
      // ignore
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 简单的刷新并发收敛：全局仅允许一个刷新 Promise 存在
let pendingRefreshPromise = null;

/**
 * 统一处理401错误（未登录或token格式错误）
 * @param {Object} error - axios错误对象
 * @param {boolean} skipRefresh - 是否跳过token刷新尝试，默认false
 * @returns {Promise} 返回Promise.reject或跳转登录
 */
function handle401Error(error, skipRefresh = false) {
  const response = error?.response;
  const responseData = response?.data || {};
  
  // 检查是否是401错误：HTTP状态码401 或 响应体中的code字段为401
  const isUnauthorized = response?.status === 401 || responseData?.code === 401;
  
  // 如果不是401错误，直接返回
  if (!isUnauthorized) {
    return Promise.reject(error);
  }
  
  // 如果是刷新token的接口返回401，直接跳转登录，不尝试刷新
  const originalRequest = error.config || {};
  const base = getBaseURL();
  const rawUrl = originalRequest.url ? String(originalRequest.url) : '';
  const url = rawUrl.startsWith(base) ? rawUrl.slice(base.length) : rawUrl;
  const isRefreshApi = /^\s*\/app\/auth\/refresh(\b|\/|\?|#|$)/.test(url);
  
  // 如果不是刷新接口且未跳过刷新，尝试刷新token
  if (!skipRefresh && !isRefreshApi && response?.status === 401) {
    try {
      pendingRefreshPromise = pendingRefreshPromise || refreshTokenRequest().catch((err) => {
        // 刷新失败，清空挂起状态并继续后续401处理，静默不打调试日志
        pendingRefreshPromise = null;
        throw err;
      });

      return pendingRefreshPromise.then(() => {
        // 刷新成功后重放原请求，附带新 token（请求拦截器会自动加）
        pendingRefreshPromise = null;
        return instance(originalRequest);
      }).catch(() => {
        // 刷新失败，走统一401逻辑
        return handle401Error(error, true);
      });
    } catch (err) {
      // ignore and fallback to login redirect
    }
  }
  
  // 根据当前路径和请求URL判断跳转到哪个登录页面
  const currentPath = window.location.pathname;

  // 特殊页面：公开价格页、微信环境提示页等，不做登录跳转
  if (currentPath === '/wechat-required' || 
      currentPath === '/price/index' || 
      currentPath === '/price/tv' ||
      currentPath.startsWith('/price/tv/')) {
    // 只提示错误，不再跳转登录
    return Promise.reject(error);
  }

  // 公开价格接口或交易时间接口，401 不跳转登录；会员盘价接口需走登录跳转
  const isPriceApi = /^\s*\/app\/price(\/?|\b)/.test(url);
  const isTradingApi = /^\s*\/app\/trading\/status(\b|\/|\?|#|$)/.test(url);
  const requestParams = originalRequest.params || {};
  const isPublicPriceRequest = requestParams.isMember === false || requestParams.isMember === 'false';
  if (isTradingApi || (isPriceApi && isPublicPriceRequest)) {
    return Promise.reject(error);
  }

  let loginPath = '/login';
  
  // 如果是后台管理页面，跳转到后台登录页
  // 判断依据：当前路径以/admin开头，或者请求URL包含/admin/或/api/
  const isAdminPath = currentPath.startsWith('/admin');
  const isAdminApi = url && (url.includes('/admin/') || url.includes('/api/auth/login') || url.startsWith('/api/'));
  
  if (isAdminPath || isAdminApi) {
    loginPath = '/admin/login';
  }
  
  // 如果已经在登录页，不重复跳转
  if ((loginPath === '/login' && (currentPath === '/login' || currentPath.startsWith('/login/'))) ||
      (loginPath === '/admin/login' && currentPath === '/admin/login')) {
    return Promise.reject(error);
  }
  
  // 清除token和用户信息
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_id');
  
  // 延迟跳转，确保提示消息能显示
  setTimeout(() => {
    // 保存当前路径作为登录后的跳转目标（排除登录页）
    const redirectPath = currentPath !== loginPath && !currentPath.startsWith('/login') 
      ? currentPath + (window.location.search || '') 
      : null;
    
    if (redirectPath) {
      window.location.href = `${loginPath}?redirect=${encodeURIComponent(redirectPath)}`;
    } else {
      window.location.href = loginPath;
    }
  }, 100);
  
  return Promise.reject(error);
}

// 响应拦截器
instance.interceptors.response.use(
  response => {
    try {
      // 捕获响应体中的 token 并持久化
      const body = (response &&  response.data) || null;
      const newToken = body && (body.token);
      if (newToken && typeof newToken === 'string') {
        localStorage.setItem('auth_token', newToken);
        const uid =  body.user.uid || body.user.id;
        if (uid) {
          localStorage.setItem('user_id', String(uid));
        }
      }
    } catch (_) {
      // ignore
    }
    // 若存在刷新挂起，先“链式等候”完成，避免紧接着的请求使用旧 token
    if (pendingRefreshPromise) {
      return pendingRefreshPromise.then(() => response);
    }
    return response;
  },
  async error => {
    // 对响应错误做点什么
    if (error.response) {
      const response = error.response;
      const responseData = response.data || {};
      
      // 统一处理401错误（包括响应体中的code: 401）
      if (response.status === 401 || responseData.code === 401) {
        return handle401Error(error);
      }
      
      // 统一处理403错误（用户被冻结等）
      if (response.status === 403 || responseData.code === 403) {
        const errorMsg = responseData?.msg || '您的账户存在异常，请联系客服';
        // 使用信息提示框显示
        try {
          const { certificationManager } = await import('./certificationManager.js');
          await certificationManager.showInfoDialog(errorMsg, '提示', {
            showCustomerServiceButton: true
          });
        } catch (e) {
          // 如果加载失败，降级使用 ElMessage
          ElMessage.error(errorMsg);
        }
        return Promise.reject(error);
      }
      
      switch (response.status) {
        case 404:
          ElMessage.error('请求的资源不存在');
          break;
        case 500:
          ElMessage.error(responseData?.msg || '服务器错误，请稍后重试');
          break;
        default:
          // 如果有错误消息，显示它
          if (responseData?.msg) {
            ElMessage.error(responseData.msg);
          }
      }
    } else if (error.request) {
      console.error('网络错误，请检查您的网络连接');
      ElMessage.error('网络错误，请检查您的网络连接');
    } else {
      console.error(`请求配置错误: ${error.message}`);
      ElMessage.error(`请求配置错误: ${error.message}`);
    }
    return Promise.reject(error);
  }
);

// 为不需要认证的实例添加响应拦截器
noAuthInstance.interceptors.response.use(
  response => {
    try {
      // 捕获无鉴权请求中的 token（如登录/模拟登录等）
      const body = (response && (response.data?.data || response.data)) || null;
      const newToken = body && (body.token || body.accessToken || body.authToken);
      if (newToken && typeof newToken === 'string') {
        localStorage.setItem('auth_token', newToken);
        const uid = (body.user && (body.user.id || body.user.userId || body.user.uid))
          || (body.userInfo && (body.userInfo.id || body.userInfo.userId || body.userInfo.uid));
        if (uid) {
          localStorage.setItem('user_id', String(uid));
        }
      }
    } catch (_) {
      // ignore
    }
    return response;
  },
  async error => {
    if (error.response) {
      const response = error.response;
      const responseData = response.data || {};
      
      // 统一处理401错误（包括响应体中的code: 401）
      // 对于noAuthInstance，直接跳转登录，不尝试刷新token
      if (response.status === 401 || responseData.code === 401) {
        return handle401Error(error, true); // skipRefresh = true
      }
      
      // 统一处理403错误（用户被冻结等）
      if (response.status === 403 || responseData.code === 403) {
        const errorMsg = responseData?.msg || '您的账户存在异常，请联系客服';
        // 使用信息提示框显示
        try {
          const { certificationManager } = await import('./certificationManager.js');
          await certificationManager.showInfoDialog(errorMsg, '提示', {
            showCustomerServiceButton: true
          });
        } catch (e) {
          // 如果加载失败，降级使用 ElMessage
          ElMessage.error(errorMsg);
        }
        return Promise.reject(error);
      }
      
      switch (response.status) {
        case 404:
          ElMessage.error('请求的资源不存在');
          break;
        case 500:
          ElMessage.error(responseData?.msg || '服务器错误，请稍后重试');
          break;
        default:
          // 如果有错误消息，显示它
          if (responseData?.msg) {
            ElMessage.error(responseData.msg);
          } else {
            ElMessage.error(`请求失败: ${error.message}`);
          }
      }
    } else if (error.request) {
      ElMessage.error('网络错误，请检查您的网络连接');
    } else {
      ElMessage.error(`请求配置错误: ${error.message}`);
    }
    return Promise.reject(error);
  }
);

// 封装 GET 请求
export function get(url, params = {}) {
  return instance.get(url, { params });
}

// 封装 POST 请求
export function post(url, data = {}) {
  return instance.post(url, data);
}

// 封装 PUT 请求
export function put(url, data = {}) {
  return instance.put(url, data);
}

// 封装 DELETE 请求
export function del(url, params = {}) {
  return instance.delete(url, { params });
}

// 导出 axios 实例
export const defHttp = instance;
export const noAuthHttp = noAuthInstance;

// 导出所有方法
export default {
  get,
  post,
  put,
  delete: del,
  instance,
  noAuthHttp
};
 